/*
 * Transactional cart mutations for PDP offers.
 *
 * Shopify's Ajax Cart API can apply part of a rejected add.  A network failure also
 * leaves the caller unable to know whether the request reached Shopify.  This module
 * consequently gives every PDP attempt a private marker, serialises mutations, and
 * always reconciles against /cart.js before it reports success or sends a customer to
 * checkout.  It intentionally never retries an add blindly.
 */

const ATTEMPT_PROPERTY = '_catharsis_attempt';
const OFFER_PROPERTY = '_catharsis_offer';
const OFFER_LABEL_PROPERTY = 'Offre';
const COMPONENT_PROPERTY = '_catharsis_component';
const GIFT_FOR_PROPERTY = '_catharsis_gift_for';
const BUNDLE_NATIVE_PROPERTY = '_catharsis_bundle_native';
const EXPECTED_ORIGINAL_PROPERTY = '_catharsis_expected_original';
const EXPECTED_FINAL_PROPERTY = '_catharsis_expected_final';
const EXPECTED_SAVINGS_PROPERTY = '_catharsis_expected_savings';
const LEGACY_PACK_PRODUCT_ID = 11185440358737;
const NATIVE_PACK_PRODUCT_ID = 11189852930385;

let queue = Promise.resolve();

export function createAttemptId() {
  if (globalThis.crypto?.randomUUID) return `ct-${globalThis.crypto.randomUUID()}`;
  return `ct-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export function lineProperty(line, name) {
  return line?.properties?.[name] ?? '';
}

export function lineQuantity(line) {
  return Number(line?.quantity || 0);
}

export function cartLinesForAttempt(cart, attemptId) {
  return (cart?.items || []).filter((line) => lineProperty(line, ATTEMPT_PROPERTY) === attemptId);
}

export function findAttemptParent(cart, attemptId, variantId) {
  return cartLinesForAttempt(cart, attemptId).filter((line) =>
    String(line.variant_id) === String(variantId) &&
    !line.parent_relationship &&
    lineProperty(line, COMPONENT_PROPERTY) === 'catlang'
  );
}

export function findAttemptGift(cart, attemptId, giftVariantId, parentKey) {
  return cartLinesForAttempt(cart, attemptId).filter((line) =>
    String(line.variant_id) === String(giftVariantId) &&
    line.parent_relationship?.parent_key === parentKey &&
    lineProperty(line, COMPONENT_PROPERTY) === 'included-catmat' &&
    lineProperty(line, GIFT_FOR_PROPERTY) === attemptId
  );
}

function discountAmount(allocation) {
  return Number(allocation?.discounted_amount ?? allocation?.amount ?? allocation?.amount_price ?? 0);
}

export function isServerFreeGift(line) {
  const finalPrice = Number(line?.final_price ?? line?.final_line_price);
  const originalPrice = Number(line?.original_price ?? line?.original_line_price);
  const quantity = lineQuantity(line);
  const finalLinePrice = Number(line?.final_line_price ?? finalPrice * quantity);
  const originalLinePrice = Number(line?.original_line_price ?? originalPrice * quantity);
  const allocations = line?.line_level_discount_allocations || line?.discount_allocations || line?.discounts || [];
  const hasDiscount = Number(line?.total_discount || 0) > 0 || allocations.some((allocation) => discountAmount(allocation) > 0);
  return quantity > 0 && finalPrice === 0 && finalLinePrice === 0 &&
    ((originalPrice === 0 && originalLinePrice === 0 && !hasDiscount) ||
      (originalPrice > 0 && originalLinePrice === originalPrice * quantity && hasDiscount));
}

function validExpectedMoney(value) {
  return Number.isSafeInteger(value) && value >= 0;
}

function expectedPriceFromLine(line) {
  const properties = line?.properties || {};
  if (![EXPECTED_ORIGINAL_PROPERTY, EXPECTED_FINAL_PROPERTY, EXPECTED_SAVINGS_PROPERTY]
    .every((key) => Object.hasOwn(properties, key) && /^\d+$/.test(String(properties[key])))) return {};
  return {
    comparePrice: Number(properties[EXPECTED_ORIGINAL_PROPERTY]),
    unitPrice: Number(properties[EXPECTED_FINAL_PROPERTY]),
    savings: Number(properties[EXPECTED_SAVINGS_PROPERTY])
  };
}

function matchesPrice(line, original, final, savings) {
  const count = lineQuantity(line);
  const originalLine = Number(line.original_line_price ?? Number(line.original_price) * count);
  const finalLine = Number(line.final_line_price ?? Number(line.final_price) * count);
  return Number(line.original_price) === original && Number(line.final_price) === final &&
    originalLine === original * count && finalLine === final * count &&
    originalLine - finalLine === savings * count &&
    Number(line.total_discount ?? savings * count) === savings * count;
}

export function validateOfferCart(cart, { attemptId, variantId, giftVariantId, quantity, requiresGift,
  unitPrice, comparePrice, savings, bundleNative = false }) {
  const parents = findAttemptParent(cart, attemptId, variantId);
  const parentQuantity = parents.reduce((sum, line) => sum + lineQuantity(line), 0);
  if (!parents.length || parents.some((line) => lineQuantity(line) <= 0) || parentQuantity !== quantity) {
    return { ok: false, reason: 'parent', parents };
  }
  const hasPriceContract = [unitPrice, comparePrice, savings].some((value) => value !== undefined);
  const expectedOriginal = comparePrice > 0 ? comparePrice : unitPrice;
  const hasExpected = validExpectedMoney(unitPrice) && validExpectedMoney(savings) &&
    validExpectedMoney(comparePrice) && expectedOriginal - unitPrice === savings;
  if (bundleNative && !hasExpected) return { ok: false, reason: 'price', parents, gifts: [] };
  if (hasPriceContract && !hasExpected) return { ok: false, reason: 'price', parents, gifts: [] };
  if (requiresGift && !giftVariantId) return { ok: false, reason: 'gift', parents, gifts: [] };
  if (hasExpected && parents.some((line) => !matchesPrice(line, expectedOriginal, unitPrice, savings))) {
    return { ok: false, reason: 'price', parents, gifts: [] };
  }
  // A BXGY qualification can split equal-price parents even when all quantities are correct.
  // The private attempt is the logical offer; API keys identify its current fragments only.
  const parent = parents[0];
  if (bundleNative && parents.some((line) =>
    lineProperty(line, OFFER_PROPERTY) !== 'france' || lineProperty(line, BUNDLE_NATIVE_PROPERTY) !== 'true')) {
    return { ok: false, reason: 'parent', parents, gifts: [] };
  }
  if (!requiresGift) {
    const complete = cartLinesForAttempt(cart, attemptId).length === parents.length;
    return complete
      ? { ok: true, parent, parents, gifts: [], quantity: parentQuantity }
      : { ok: false, reason: 'unexpected', parent, parents, gifts: [] };
  }
  const parentKeys = new Set(parents.map((line) => line.key));
  const gifts = cartLinesForAttempt(cart, attemptId).filter((line) =>
    String(line.variant_id) === String(giftVariantId) &&
    parentKeys.has(line.parent_relationship?.parent_key) &&
    lineProperty(line, OFFER_PROPERTY) === 'france' &&
    lineProperty(line, COMPONENT_PROPERTY) === 'included-catmat' &&
    lineProperty(line, GIFT_FOR_PROPERTY) === attemptId
  );
  const giftQuantity = gifts.reduce((sum, line) => sum + lineQuantity(line), 0);
  const complete = cartLinesForAttempt(cart, attemptId).length === parents.length + gifts.length;
  const free = gifts.length > 0 && gifts.every((line) => lineQuantity(line) > 0 && isServerFreeGift(line) &&
    (!hasExpected || savings === 0 ||
      (Number(line.original_price) === 0 && Number(line.total_discount || 0) === 0)));
  if (parents.some((line) => lineProperty(line, OFFER_PROPERTY) !== 'france') ||
      giftQuantity !== quantity || !free || !complete) {
    return { ok: false, reason: 'gift', parent, parents, gifts };
  }
  return { ok: true, parent, parents, gifts, quantity: parentQuantity };
}

export function invalidFrenchPackAttempts(cart) {
  if ((cart?.items || []).some((line) =>
    Number(line.product_id) === LEGACY_PACK_PRODUCT_ID ||
    (Number(line.product_id) === NATIVE_PACK_PRODUCT_ID && !lineProperty(line, ATTEMPT_PROPERTY)))) {
    return ['unmarked-pack'];
  }
  const frenchLines = (cart?.items || []).filter((line) => lineProperty(line, OFFER_PROPERTY) === 'france');
  if (frenchLines.some((line) => !lineProperty(line, ATTEMPT_PROPERTY))) return ['unmarked'];
  const attempts = new Set(frenchLines.map((line) => lineProperty(line, ATTEMPT_PROPERTY)).filter(Boolean));
  return [...attempts].filter((attemptId) => {
    const { parents, gifts } = markedFrenchPair(cart, attemptId);
    if (!parents.length) return true;
    const bundleNative = parents.some((line) => lineProperty(line, BUNDLE_NATIVE_PROPERTY) === 'true');
    // The draft's promised 56 € checkout saving exists only on the Shopify Bundle.
    // Old BXGY pairs can be free yet save only 19,99 €; they must be reselected.
    if (!bundleNative) return true;
    // Both variants must remain uniform across ALL fragments; the full validator rejects
    // unknown components, a gift attached to another attempt, paid units, and excess units.
    return !validateOfferCart(cart, {
      attemptId,
      variantId: parents[0].variant_id,
      giftVariantId: gifts[0]?.variant_id,
      quantity: parents.reduce((sum, line) => sum + lineQuantity(line), 0),
      requiresGift: !bundleNative,
      bundleNative,
      ...expectedPriceFromLine(parents[0])
    }).ok;
  });
}

function propertiesFromForm(form) {
  const properties = {};
  if (typeof HTMLFormElement === 'undefined' || !(form instanceof HTMLFormElement)) return properties;
  new FormData(form).forEach((value, key) => {
    const match = /^properties\[([^\]]+)\]$/.exec(key);
    if (match && typeof value === 'string' && value !== '') properties[match[1]] = value;
  });
  return properties;
}

export function offerRequestFromForm(form, offer, attemptId) {
  const quantityInput = form?.querySelector('[name="quantity"]');
  const rawQuantity = Number.parseInt(quantityInput?.value || '1', 10);
  const quantity = Number.isSafeInteger(rawQuantity) && rawQuantity > 0 ? rawQuantity : 1;
  const properties = {
    ...propertiesFromForm(form),
    [ATTEMPT_PROPERTY]: attemptId,
    [OFFER_PROPERTY]: offer.offerId || 'essential',
    [OFFER_LABEL_PROPERTY]: offer.label || '',
    _catharsis_component: 'catlang'
  };
  if (offer.bundleNative === true) properties[BUNDLE_NATIVE_PROPERTY] = 'true';
  if (offer.offerId === 'france' && validExpectedMoney(offer.unitPrice) &&
      validExpectedMoney(offer.regularPrice ?? offer.comparePrice) && validExpectedMoney(offer.savings)) {
    properties[EXPECTED_ORIGINAL_PROPERTY] = String(offer.regularPrice ?? offer.comparePrice);
    properties[EXPECTED_FINAL_PROPERTY] = String(offer.unitPrice);
    properties[EXPECTED_SAVINGS_PROPERTY] = String(offer.savings);
  }
  return {
    id: String(offer.variantId),
    quantity,
    properties
  };
}

function fetchJson(fetchImpl, url, init) {
  return fetchImpl(url, init).then(async (response) => {
    let body = null;
    try { body = await response.json(); } catch (_) { /* status remains useful */ }
    if (!response.ok) {
      const error = new Error(body?.description || body?.message || `Cart request failed (${response.status})`);
      error.status = response.status;
      error.body = body;
      throw error;
    }
    return body;
  });
}

export function cartApi(fetchImpl = fetch) {
  return {
    read() { return fetchJson(fetchImpl, '/cart.js', { headers: { Accept: 'application/json' } }); },
    add(item) {
      return fetchJson(fetchImpl, '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [item] })
      });
    },
    change(key, quantity) {
      return fetchJson(fetchImpl, '/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity })
      });
    }
  };
}

async function rollbackAttempt(api, attemptId) {
  // Only lines carrying this exact, private attempt marker are ours to remove.
  // Re-read after every mutation: a parent removal cascades to its child and changes keys.
  for (let pass = 0; pass < 8; pass += 1) {
    const cart = await api.read();
    const line = cartLinesForAttempt(cart, attemptId)[0];
    if (!line) return cart;
    try { await api.change(line.key, 0); } catch (_) { /* a rejected removal can still mutate; re-read */ }
  }
  return api.read();
}

async function reconcile(api, spec) {
  const cart = await api.read();
  return { cart, validation: validateOfferCart(cart, spec) };
}

export async function runOfferTransaction({ api = cartApi(), form, offer, onCart, onError } = {}) {
  if (!form || !offer?.variantId) throw new Error('Offre indisponible.');
  const attemptId = createAttemptId();
  // The selected offer owns its variant. The form can still contain the page product.
  const currentOffer = { ...offer };
  const parent = offerRequestFromForm(form, currentOffer, attemptId);
  const bundleNative = currentOffer.offerId === 'france' && currentOffer.bundleNative === true;
  const requiresGift = currentOffer.offerId === 'france' && !bundleNative;
  const spec = { attemptId, variantId: parent.id, giftVariantId: offer.giftVariantId,
    quantity: parent.quantity, requiresGift, bundleNative, unitPrice: offer.unitPrice,
    comparePrice: offer.regularPrice ?? offer.comparePrice, savings: offer.savings };
  let attemptStarted = false;
  let committed = false;

  try {
    if (bundleNative) {
      const before = await api.read();
      if ((before?.items || []).some((line) =>
        lineProperty(line, OFFER_PROPERTY) === 'france' &&
        lineProperty(line, BUNDLE_NATIVE_PROPERTY) !== 'true')) {
        throw new Error('Retirez d’abord votre ancien Pack Français du panier avant d’ajouter cette formule.');
      }
    }
    // The parent has to exist alone before the child is sent.  parent_id is ambiguous for
    // two same-variant parents; parent_line_key after a fresh read is not.
    attemptStarted = true;
    try { await api.add(parent); } catch (_) { /* reconcile covers partial/lost responses */ }
    let outcome = await reconcile(api, spec);
    if (!outcome.validation.ok && outcome.validation.reason !== 'gift') {
      throw new Error('L’article n’a pas pu être ajouté dans la quantité demandée. Vérifiez le panier puis réessayez.');
    }

    if (requiresGift) {
      const parentKey = outcome.validation.parent.key;
      const gift = {
        id: String(offer.giftVariantId),
        quantity: parent.quantity,
        parent_line_key: parentKey,
        properties: {
          [ATTEMPT_PROPERTY]: attemptId,
          [OFFER_PROPERTY]: 'france',
          [OFFER_LABEL_PROPERTY]: offer.label || '',
          _catharsis_component: 'included-catmat',
          _catharsis_gift_for: attemptId
        }
      };
      try { await api.add(gift); } catch (_) { /* non-atomic failures are reconciled below */ }
      outcome = await reconcile(api, spec);
      if (!outcome.validation.ok) {
        let removed = false;
        try {
          const afterRollback = await rollbackAttempt(api, attemptId);
          removed = cartLinesForAttempt(afterRollback, attemptId).length === 0;
        } catch (_) { /* An unreadable cart cannot prove removal. */ }
        throw new Error(removed
          ? 'Le Pack Français n’est pas disponible dans la quantité demandée. Aucun article de cette tentative n’a été conservé.'
          : 'Le Pack Français n’a pas pu être confirmé. Vérifiez votre panier avant de poursuivre.');
      }
    }

    // A regular offer may still have been affected by a lost/partial response.  Never infer
    // success from HTTP alone; only the marked cart state is authoritative.
    if (!outcome.validation.ok) {
      await rollbackAttempt(api, attemptId);
      throw new Error('Le panier n’a pas pu être confirmé. Vérifiez votre panier puis réessayez.');
    }
    committed = true;
    onCart?.(outcome.cart);
    return { ...outcome, attemptId };
  } catch (error) {
    // A 422 or lost response can leave a partial parent behind.  No failure after an add is
    // allowed to skip this cleanup; rollback only sees the fresh private marker, never older
    // customer lines.  Keep the original error even if Shopify is temporarily unreadable.
    if (attemptStarted && !committed) {
      try { await rollbackAttempt(api, attemptId); } catch (_) { /* next cart read remains authoritative */ }
    }
    onError?.(error);
    throw error;
  }
}

export function enqueueCartMutation(work) {
  const current = queue.then(work, work);
  queue = current.catch(() => undefined);
  return current;
}

function markedFrenchPair(cart, attemptId) {
  const lines = cartLinesForAttempt(cart, attemptId);
  const parents = lines.filter((item) =>
    !item.parent_relationship &&
    lineProperty(item, OFFER_PROPERTY) === 'france' &&
    lineProperty(item, COMPONENT_PROPERTY) === 'catlang'
  );
  const gifts = lines.filter((item) =>
    Boolean(item.parent_relationship) &&
    lineProperty(item, OFFER_PROPERTY) === 'france' &&
    lineProperty(item, COMPONENT_PROPERTY) === 'included-catmat' &&
    lineProperty(item, GIFT_FOR_PROPERTY) === attemptId
  );
  return { parents, gifts };
}

function pairSpecFromLine(cart, line) {
  const attemptId = lineProperty(line, ATTEMPT_PROPERTY);
  if (!attemptId || lineProperty(line, OFFER_PROPERTY) !== 'france') return null;
  const { parents, gifts } = markedFrenchPair(cart, attemptId);
  const parent = parents.find((item) => item.key === line.key) || parents.find((item) =>
    gifts.some((gift) => gift.parent_relationship?.parent_key === item.key)
  );
  const gift = gifts.find((item) => item.key === line.key) || gifts.find((item) =>
    item.parent_relationship?.parent_key === parent?.key
  ) || gifts.find((item) => parents.some((candidate) => candidate.key === item.parent_relationship?.parent_key));
  if (!parent || !gift) return null;
  return {
    attemptId,
    variantId: String(parent.variant_id),
    giftVariantId: String(gift.variant_id),
    parent,
    gift
  };
}

async function changeThenRead(api, key, quantity) {
  try { await api.change(key, quantity); } catch (_) { /* a lost response still needs reconciliation */ }
  return api.read();
}

function quantityCorrection(lines, desired, preferRemove = () => false) {
  const total = lines.reduce((sum, item) => sum + lineQuantity(item), 0);
  if (total === desired || !lines.length) return null;
  // A discount split is several API lines for ONE logical offer. Setting every split to the
  // requested quantity multiplies items. Apply only the remaining delta to a fresh key.
  const sorted = [...lines].sort((a, b) => Number(preferRemove(b)) - Number(preferRemove(a)));
  const line = sorted[0];
  return { key: line.key, quantity: Math.max(0, lineQuantity(line) + desired - total) };
}

export async function reconcileNestedQuantity({ api = cartApi(), key, quantity }) {
  if (!Number.isSafeInteger(quantity) || quantity < 0) throw new Error('Quantité invalide.');
  const before = await api.read();
  const line = (before.items || []).find((item) => item.key === key);
  if (!line) throw new Error('Cette ligne n’existe plus dans le panier.');
  const initialPair = pairSpecFromLine(before, line);
  const french = lineProperty(line, OFFER_PROPERTY) === 'france';
  if (!french) return changeThenRead(api, key, quantity);
  // Even an incomplete pack must remain removable. Every fragment of its exact attempt is
  // removed; a parent with a missing child must never strand an unremovable cart line.
  const attemptId = lineProperty(line, ATTEMPT_PROPERTY);
  if (quantity === 0 && attemptId) {
    const cart = await rollbackAttempt(api, attemptId);
    if (!cartLinesForAttempt(cart, attemptId).length) return cart;
    throw new Error('Le Pack Français n’a pas pu être retiré. Réessayez avant de poursuivre.');
  }
  if (lineProperty(line, BUNDLE_NATIVE_PROPERTY) === 'true') {
    const spec = {
      attemptId,
      variantId: String(line.variant_id),
      quantity,
      requiresGift: false,
      bundleNative: true,
      ...expectedPriceFromLine(line)
    };
    const currentQuantity = cartLinesForAttempt(before, attemptId)
      .reduce((sum, item) => sum + lineQuantity(item), 0);
    if (!validateOfferCart(before, { ...spec, quantity: currentQuantity }).ok) {
      throw new Error('Ce Pack Français est incomplet. Retirez-le du panier puis ajoutez-le à nouveau.');
    }
    let cart = before;
    try {
      for (let pass = 0; pass < 12; pass += 1) {
        if (validateOfferCart(cart, spec).ok) return cart;
        const lines = findAttemptParent(cart, attemptId, spec.variantId);
        const change = quantityCorrection(lines, quantity);
        if (!change) break;
        const fingerprint = JSON.stringify(cartLinesForAttempt(cart, attemptId));
        cart = await changeThenRead(api, change.key, change.quantity);
        if (JSON.stringify(cartLinesForAttempt(cart, attemptId)) === fingerprint) break;
      }
      if (validateOfferCart(cart, spec).ok) return cart;
      throw new Error('Synchronisation incomplète.');
    } catch (_) {
      let removed = false;
      try {
        cart = await rollbackAttempt(api, attemptId);
        removed = !cartLinesForAttempt(cart, attemptId).length;
      } catch (_) { /* a failed cleanup must not allow checkout */ }
      throw new Error(removed
        ? 'La quantité du Pack Français n’a pas pu être synchronisée. Ce pack a été retiré ; vos autres articles sont conservés. Ajoutez-le à nouveau.'
        : 'La quantité du Pack Français n’a pas pu être synchronisée. Vérifiez votre panier avant de poursuivre.');
    }
  }
  // Never treat an incomplete Pack as an ordinary line: that would bypass gift validation.
  if (!initialPair) throw new Error('Ce Pack Français est incomplet. Retirez-le du panier puis ajoutez-le à nouveau.');

  const spec = {
    attemptId: initialPair.attemptId,
    variantId: initialPair.variantId,
    giftVariantId: initialPair.giftVariantId,
    quantity,
    requiresGift: true,
    ...expectedPriceFromLine(initialPair.parent)
  };
  let cart = before;
  try {
    // Shopify can split either parent OR child as discounts are recalculated. Discover all
    // fragments with our exact attempt/component/relationship markers on every read. No
    // variant-only fallback can select another customer's line or another offer attempt.
    for (let pass = 0; pass < 12; pass += 1) {
      if (validateOfferCart(cart, spec).ok) return cart;
      const pair = markedFrenchPair(cart, spec.attemptId);
      const parentKeys = new Set(pair.parents.map((item) => item.key));
      if (!pair.parents.length || !pair.gifts.length ||
          pair.parents.some((item) => String(item.variant_id) !== spec.variantId) ||
          pair.gifts.some((item) => String(item.variant_id) !== spec.giftVariantId || !parentKeys.has(item.parent_relationship.parent_key)) ||
          cartLinesForAttempt(cart, spec.attemptId).length !== pair.parents.length + pair.gifts.length) break;
      const parentChange = quantityCorrection(pair.parents, quantity,
        (item) => !pair.gifts.some((gift) => gift.parent_relationship.parent_key === item.key));
      // Prefer removing charged excess fragments so a 2→1 change cannot leave a paid gift.
      const giftChange = quantityCorrection(pair.gifts, quantity, (item) => !isServerFreeGift(item));
      const change = parentChange || giftChange;
      if (!change) break;
      const fingerprint = JSON.stringify(cartLinesForAttempt(cart, spec.attemptId));
      cart = await changeThenRead(api, change.key, change.quantity);
      if (JSON.stringify(cartLinesForAttempt(cart, spec.attemptId)) === fingerprint) break;
    }
    if (validateOfferCart(cart, spec).ok) return cart;
    throw new Error('Synchronisation incomplète.');
  } catch (_) {
    // Do not leave a newly charged "gift" available to accelerated checkout after a partial
    // 422, network error, or irreconcilable split. Remove ONLY this logical attempt. A failed
    // cleanup still throws and is rejected independently by the checkout cart validator.
    let removed = false;
    try {
      cart = await rollbackAttempt(api, spec.attemptId);
      removed = !cartLinesForAttempt(cart, spec.attemptId).length;
    } catch (_) { /* checkout remains blocked until a server read proves the pair valid */ }
    throw new Error(removed
      ? 'La quantité du Pack Français n’a pas pu être synchronisée. Ce pack a été retiré ; vos autres articles sont conservés. Ajoutez-le à nouveau.'
      : 'La quantité du Pack Français n’a pas pu être synchronisée. Vérifiez votre panier avant de poursuivre.');
  }
}

if (typeof window !== 'undefined') {
  window.CatharsisCartTransactions = {
    ATTEMPT_PROPERTY,
    enqueueCartMutation,
    runOfferTransaction,
    reconcileNestedQuantity,
    invalidFrenchPackAttempts,
    cartApi
  };
}
